import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { SubmissionDetails } from './SubmissionDetailsTab';
import ProtectedAction from 'src/components/ProtectedAction';
import { Button, ButtonText, useToast } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faCheck,
  faPeopleArrows
} from '@fortawesome/pro-regular-svg-icons';
import { useReviewData } from 'src/contexts/ReviewDataProvider';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from 'src/components/AuthHOC';
import {
  useAcceptReviewRequest,
  useReviewInvitationDecline
} from 'src/api/commands';
import Invitation from './Invitation';
import { useQuery } from '@tanstack/react-query';
import { fetchReviewInvitationPreview } from 'src/api/queries';
import { IEmailLog } from '@publishing-engine/domain/types/email-template';

const InvitationPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useKeycloak();
  const toast = useToast();
  const {
    review,
    submissionId,
    submissionContext,
    invalidateReviewContextData,
    organizationId
  } = useReviewData();

  const isCoReviewEnabled = submissionContext.config.isCoReviewEnabled;

  const queryKey = ['review', { id: review.id }];

  const acceptReviewRequestMutation = useAcceptReviewRequest({
    submissionId,
    reviewId: review.id,
    organizationId,
    submissionContextId: submissionContext.id,
    queryKey
  });

  const declineReviewMutation = useReviewInvitationDecline({
    reviewId: review.id,
    userId: currentUser.id as string,
    queryKey
  });

  const declineReview = async () => {
    const { id, submissionContextId } = review;
    const submissionId = review.caseData.submission.id;

    try {
      const response = await declineReviewMutation.mutateAsync();

      if (response) {
        navigate(
          `/reviews/${id}/decline-review?submissionId=${submissionId}&ctxId=${submissionContextId}&isInvitation=true`
        );
      }
    } catch (_err) {
      toast({
        variant: 'attention',
        title: 'Decline Failed',
        description: 'Review decline failed. Please try again after sometime.',
        isClosable: true
      });
    }
  };

  const acceptInvitation = async () => {
    const res = await acceptReviewRequestMutation.mutateAsync();
    if (res.isSuccess) {
      invalidateReviewContextData();
    } else {
      toast({
        variant: 'error',
        title: 'Error while accepting the invitation',
        description: 'Please refresh the page and try again.',
        isClosable: true
      });
    }
    return res;
  };

  const acceptWithCoReview = () => {
    navigate(`/app/reviews/${review.id}/coreview-invite`);
  };

  const handleAcceptWithCoReview = async () => {
    const res = await acceptInvitation();
    if (res.isSuccess) {
      acceptWithCoReview();
    } else {
      toast({
        variant: 'error',
        title: 'Error while accepting the invitation',
        description: 'Please try again later.',
        isClosable: true
      });
    }
  };

  const { data: invitationPreviewData } = useQuery<{ results: IEmailLog[] }>({
    queryKey: [
      'invitation-preview',
      { reviewId: review.id, email: currentUser.email }
    ],
    queryFn: () => fetchReviewInvitationPreview(review.id, currentUser.email),
    enabled: !!review.id && !!currentUser.email
  });

  return (
    <>
      <Flex
        minHeight="100vh"
        backgroundColor="white"
        width="100%"
        flexDirection={{ base: 'column', xl: 'row' }}
      >
        <Box
          width={{ base: '100%', xl: '50%' }}
          minWidth={0}
          backgroundColor="white"
        >
          <Box p={6}>
            <Text textStyle="headingTitle" mb={2}>
              Invitation to Review
            </Text>
            <SubmissionDetails />
          </Box>
        </Box>
        <Box
          width={{ base: '100%', xl: '50%' }}
          minWidth={0}
          backgroundColor="surfaceSecondaryFill"
        >
          <Invitation body={invitationPreviewData?.results} />
        </Box>
      </Flex>

      <Flex
        padding={4}
        bottom={0}
        zIndex={1}
        justifyContent="center"
        position="sticky"
        bgColor="surfacePrimaryFill"
        marginX="auto"
      >
        <Flex
          width="100%"
          justifyContent={{ base: 'stretch', xl: 'flex-end' }}
          flexDirection={{ base: 'column', xl: 'row' }}
          gap={2}
        >
          <ProtectedAction
            relation="canReview"
            entityType="review"
            entityId={review.id}
          >
            <ButtonText
              size="context"
              variant="caution"
              onClick={declineReview}
              disabled={acceptReviewRequestMutation.isLoading}
            >
              <FontAwesomeIcon icon={faBan} />
              Decline
            </ButtonText>
          </ProtectedAction>
          {isCoReviewEnabled ? (
            <ProtectedAction
              relation="canReview"
              entityType="review"
              entityId={review.id}
            >
              <Button variant="secondary" onClick={handleAcceptWithCoReview}>
                <FontAwesomeIcon icon={faPeopleArrows} />
                Accept with Co-Review
              </Button>
            </ProtectedAction>
          ) : null}
          <ProtectedAction
            relation="canReview"
            entityType="review"
            entityId={review.id}
          >
            <Button
              size="context"
              variant="primary"
              onClick={acceptInvitation}
              isLoading={acceptReviewRequestMutation.isLoading}
              isDisabled={acceptReviewRequestMutation.isLoading}
            >
              <FontAwesomeIcon icon={faCheck} />
              Accept Invitation
            </Button>
          </ProtectedAction>
        </Flex>
      </Flex>
    </>
  );
};

export default InvitationPage;
